// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

import { Workbox } from "workbox-window";

import {
  recordBoot,
  recordRegistration,
  reportUpdateCheckFailed,
  trackRegistration,
  watchInstallOutcome,
} from "./registrationTelemetry";

// Prefer the `log` helpers below over this; they prefix every message with [sw].
const rawConsole = console;

// Alias methods via the receiver so `this` is always bound correctly —
// destructuring first can throw "Illegal invocation" in some engines.
const log = {
  debug: (...args: unknown[]) => rawConsole.debug("[sw]", ...args),
  warn: (...args: unknown[]) => rawConsole.warn("[sw]", ...args),
  error: (...args: unknown[]) => rawConsole.error("[sw]", ...args),
};

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === "[::1]" ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
  ),
);

type Config = {
  checkForUpdatesInterval: number;
  onRegistered?: () => void;
};

type UpdateListener = (onAccept: () => void) => void;

// How long to wait (ms) for the accepted SW to take over before reloading anyway.
const ACTIVATION_TIMEOUT_MS = 3_000;

// Listeners interested in "a new SW is waiting; here's how to activate it."
// Pub/sub keeps this module UI-agnostic and lets consumers come and go
// without the module caring about their lifecycle.
const updateListeners = new Set<UpdateListener>();

// Latest outstanding notification, held so that subscribers who attach
// AFTER `notifyWaiting` fires (e.g. React effects that run post-commit while
// `wb.register()` already detected a waiting SW) still see the pending
// update. The closure is safe to replay — it resolves its target at call
// time and falls back to reload if there's nothing to message.
let pendingNotification: (() => void) | undefined;

export function subscribeToUpdates(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  if (pendingNotification) {
    listener(pendingNotification);
  }
  return () => {
    updateListeners.delete(listener);
  };
}

export function register(config: Config) {
  if (import.meta.env.PROD) {
    recordBoot();
  }
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(import.meta.env.BASE_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener("load", () => {
      // Do not change the name of the generated service worker file!
      // This can cause issues with service worker caching.
      // https://web.dev/service-worker-lifecycle/#avoid-url-change
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          log.debug("app is being served cache-first by a service worker");
          log.warn(
            "running on localhost. This should be used for testing only. It can otherwise cause unexpected behaviour due to caching of resources.",
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function runPeriodicUpdates(workbox: Workbox, delay: number) {
  setInterval(async () => {
    try {
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;
      if (!navigator.onLine) {
        return;
      }
      const resp = await fetch(swUrl, {
        method: "HEAD",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      if (resp?.status !== 200) {
        return;
      }
    } catch (_e) {
      // Offline or a flaky network; the next interval tries again.
      return;
    }
    try {
      await workbox.update();
    } catch (error) {
      reportUpdateCheckFailed(error);
    }
  }, delay);
}

async function registerValidSW(swUrl: string, config: Config) {
  try {
    // Check to see if the page is currently controlled by a service worker.
    let isControlled = Boolean(navigator.serviceWorker.controller);

    const wb = new Workbox(swUrl);
    let registration: ServiceWorkerRegistration | undefined;

    // Guard against an infinite refresh loop with Chrome DevTools'
    // "Update on Reload" — without this, controllerchange → reload →
    // controllerchange → … loops indefinitely. The timeout releases the
    // guard if `reload()` was swallowed (BFCache restore, beforeunload
    // prompt cancelled, etc.) so the next activation can still refresh.
    let preventDevToolsReloadLoop = false;

    // On a page that loaded uncontrolled (a hard reload bypasses the SW, so
    // `controller` stays null for its whole life) the initial-controllerchange
    // pass below would otherwise swallow an update the user asked for.
    let activationRequested = false;

    wb.addEventListener("controlling", () => {
      if (!isControlled && !activationRequested) {
        isControlled = true;
        log.debug("initial controllerchange; no reload");
        return;
      }
      if (preventDevToolsReloadLoop) {
        log.debug("controllerchange while reload guard is up; skip");
        return;
      }
      preventDevToolsReloadLoop = true;
      log.debug("controllerchange → reload");
      window.location.reload();
      setTimeout(() => {
        log.debug("reload timeout expired; release guard");
        preventDevToolsReloadLoop = false;
      }, 5000);
    });

    // Track SWs we've already surfaced a toast for, so subsequent state
    // transitions on the same worker (activating/activated/redundant) don't
    // re-trigger notifications.
    const notifiedSWs = new WeakSet<ServiceWorker>();

    const notifyWaiting = (snapshotSW: ServiceWorker) => {
      if (notifiedSWs.has(snapshotSW)) return;
      notifiedSWs.add(snapshotSW);
      log.debug("surfacing update toast for waiting SW");

      const onAccept = () => {
        // Prefer the registration's live `waiting` at click time — an even
        // newer SW may have replaced the one this toast was shown for.
        const liveWaiting = registration?.waiting ?? undefined;
        const target =
          liveWaiting && liveWaiting.state === "installed"
            ? liveWaiting
            : snapshotSW.state === "installed"
              ? snapshotSW
              : undefined;

        if (target) {
          log.debug(
            `toast accepted; posting SKIP_WAITING to ${
              target === liveWaiting ? "live waiting" : "snapshot"
            } SW`,
          );
          activationRequested = true;
          target.postMessage({ type: "SKIP_WAITING" });
          // Nothing guarantees the activation, or the clientsClaim that
          // follows it, ever reaches this page; a dead click is worse than a
          // redundant reload.
          window.setTimeout(() => {
            log.warn("SW did not take over after SKIP_WAITING; reloading");
            window.location.reload();
          }, ACTIVATION_TIMEOUT_MS);
          return;
        }

        // The waiting SW was activated elsewhere (another tab) or became
        // redundant between the toast being shown and the click. Reload so
        // the user isn't stuck on a no-op click.
        log.warn("No installed service worker to activate; reloading.");
        window.location.reload();
      };

      pendingNotification = onAccept;
      updateListeners.forEach((listener) => listener(onAccept));
    };

    // Watch an installing SW and surface the toast once it reaches the
    // `installed` (waiting) state. This replaces workbox-window's `waiting`
    // event — workbox removes its own `updatefound` listener after the
    // first "external" update, and every periodic check qualifies as
    // external (they fire >60s after register). That silently swallows
    // every update after the first one.
    const watchedSWs = new WeakSet<ServiceWorker>();
    const watchInstallingSW = (sw: ServiceWorker) => {
      if (watchedSWs.has(sw)) return;
      watchedSWs.add(sw);
      watchInstallOutcome(sw);
      log.debug(`watching installing SW (state=${sw.state})`);
      const check = () => {
        if (sw.state === "installed" && registration?.waiting === sw) {
          notifyWaiting(sw);
          sw.removeEventListener("statechange", check);
          return;
        }
        // SW moved past `installed` without becoming our waiting worker
        // (e.g. superseded by a newer install, or activated directly).
        // Nothing further to observe.
        if (sw.state === "redundant" || sw.state === "activated") {
          sw.removeEventListener("statechange", check);
        }
      };
      sw.addEventListener("statechange", check);
      check();
    };

    registration = await trackRegistration(() => wb.register());
    log.debug("register complete", {
      hasWaiting: !!registration?.waiting,
      hasInstalling: !!registration?.installing,
      controller: !!navigator.serviceWorker.controller,
    });

    if (!registration) return;

    recordRegistration(registration);

    // Handle a SW that was already waiting before this page loaded.
    if (registration.waiting?.state === "installed") {
      const searchParams = new URLSearchParams(window.location.search);
      const isCheckoutRoute = searchParams.get("checkout") !== null;
      if (!isCheckoutRoute) {
        // Auto-activate. `clientsClaim` in the SW fires `controllerchange`,
        // which our `controlling` handler turns into a reload.
        log.debug("SW already waiting at register; auto-activating");
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      } else {
        // Avoid disrupting checkout — surface the toast so the user can
        // update once they're done.
        log.debug("SW already waiting at register; on checkout route");
        notifyWaiting(registration.waiting);
      }
    }

    // Our own `updatefound` listener persists for the lifetime of the page,
    // so we catch every update (v2, v3, v4 …) not just the first one.
    registration.addEventListener("updatefound", () => {
      log.debug("updatefound");
      if (registration?.installing) {
        watchInstallingSW(registration.installing);
      }
    });

    // Cover the case where a SW is mid-install at register time.
    if (registration.installing) {
      watchInstallingSW(registration.installing);
    }

    if (typeof config.onRegistered === "function") {
      config.onRegistered();
    }

    // Check for updates periodically
    runPeriodicUpdates(wb, config.checkForUpdatesInterval);
  } catch (error) {
    log.error("registration failed:", error);
  }
}

function checkValidServiceWorker(swUrl: string, config: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      log.debug("no internet connection; running in offline mode");
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        log.error(error.message);
      });
  }
}
